import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type NotificationFilterState as FilterType, type TimeFilterValue } from "@/domain";
import { Dropdown } from "@/components/ui";
import type { FilterOption } from "@/domain/types";

type ReadFilterValue = "all" | "unread" | "read";

interface NotificationFiltersProps {
  filters: FilterType;
  timeFilter: TimeFilterValue;
  onReadFilterChange: (value: boolean | undefined) => void;
  onTimeFilterChange: (value: TimeFilterValue) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
}

export const NotificationFilters = ({
  filters,
  timeFilter,
  onReadFilterChange,
  onTimeFilterChange,
  onClear,
  hasActiveFilters,
}: NotificationFiltersProps) => {
  const { t } = useTranslation();

  const currentReadFilter: ReadFilterValue =
    filters.is_read === undefined ? "all" : filters.is_read ? "read" : "unread";

  const handleReadChange = (value: string) => {
    onReadFilterChange(value === "all" ? undefined : value === "read");
  };

  const handleTimeChange = (value: string) => {
    onTimeFilterChange(value as TimeFilterValue);
  };

  const readOptions: FilterOption[] = useMemo(
    () => [
      { value: "all", label: t("notifications.all_notifications") },
      { value: "unread", label: t("notifications.unread_only") },
      { value: "read", label: t("notifications.read_only") },
    ],
    [t],
  );

  const timeOptions: FilterOption[] = useMemo(
    () => [
      { value: "all", label: t("notifications.all_time") },
      { value: "this_week", label: t("notifications.this_week") },
      { value: "this_month", label: t("notifications.this_month") },
    ],
    [t],
  );

  return (
    <div className="px-3 py-2 space-y-2 min-w-[180px]">
      {/* Status filter */}
      <Dropdown
        options={readOptions}
        value={currentReadFilter}
        onChange={handleReadChange}
        placeholder={t("notifications.all_notifications")}
        size="sm"
      />

      {/* Time filter */}
      <Dropdown
        options={timeOptions}
        value={timeFilter}
        onChange={handleTimeChange}
        placeholder={t("notifications.all_time")}
        size="sm"
      />

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          onClick={onClear}
          className="w-full py-1 text-xs text-text-tertiary hover:text-text-primary underline"
        >
          {t("notifications.clear_filters")}
        </button>
      )}
    </div>
  );
};
